import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Card, Row, Col, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { ApproverCommentModal } from '../../../pages/Absences/components/ApproverCommentModal';
import {
  AbsenceRequestFilterType,
  AbsenceTypeMap,
  defaultFilter,
} from './types';
import { useManagerReportsMode } from '../../../hooks/useManagerReportsMode';
import {
  AbsenceApprovalStatus,
  ApprovalRequestStatus,
  useAbsencesForManagerQuery,
} from '../../../__generated__/graphql';
import ServerError from '../../../../../components/ServerError';
import {
  mapAbsenceRequestFilterInput,
  mapAbsenceRequests,
  mapSortBy,
} from './mappings';
import { routes as mainRoutes } from '../../../routes';
import { AbsenceRequestFiltersConnected } from './Filters/AbsenceRequestFiltersConnected';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { useFlairTableWithPaginationAndFilter } from '../../../../../hooks/useFlairTableWithPaginationAndFilter';
import { Maybe } from '../../../../../utils/maybe';
import { LoomVideo } from '../../../components/LoomVideo';
import LoomVideoPreviewInModal from '../../../components/LoomVideo/LoomVideoPreviewInModal';

import { AbsenceRequest } from '../types';
import { EmptyAbsenceRequestsCard } from '../EmptyAbsenceRequestsCard';
import { useAbsenceRequestTableRenderSettings } from '../useAbsenceRequestTableRenderSettings';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsTable,
  ExtraTableRenderSettings,
} from '../../../components/ExtraFields';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { useObjectLabels } from '../../../hooks/useObjectLabels';
import { ReviewActionsCell } from '../ReviewActionsCell';
import { useApproveAbsenceRequestTable } from '../useAbsencesRequestsTable';
import { ExtraFieldsTableId } from '../../../components/ExtraFields/types';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import AbsenceStatus from '../../../pages/Absences/components/AbsenceStatus';
import { EmployeeWithAvatar } from '../../../components/Employee/EmployeeWithAvatar';
import { CellProps, Column, Renderer } from 'react-table';
import { ExtraTableItemType } from '../../../components/ExtraFields/types';
import AbsenceDateTimeRange from '../../../pages/Absences/components/AbsenceDateTimeRange';
import FormattedDateTime from '../../../../../components/datetime/FormattedDateTime';
import { useWindowWidth } from '@react-hook/window-size';
import { MOBILE_BREAKPOINT } from '../common';
import { TableContentManual } from '../../components/TableContentManual';
import MultiDayAbsenceDetails from '../../../pages/Absences/components/MultiDayAbsenceDetails';
import { useUserInfo } from '../../../context/UserInfo';
import { ApprovalRequestDetailsSidebar } from '../../../pages/ApprovalRequests/ApprovalRequestsTable/ApprovalRequestDetailsSidebar';
import { RelatedObjectNames } from '../../../components/Comment/types';
import { useApprovalRequestSidebar } from '../PendingRequests/useApprovalRequestSidebar';
import { ApprovalAvatars } from './ApprovalAvatars';
import { AbsenceTypeText } from '../../../pages/Absences/components/AbsenceTypeCell';

const PAGE_SIZE = 50;

const i18Path = 'absences.myAbsences.table';

export const AbsenceRequestsTable: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const [selectedLoomVideo, setSelectedLoomVideo] =
    useState<Maybe<LoomVideo>>(null);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const t = useNamespacedTranslation(i18Path);
  const objectLabels = useObjectLabels();
  const { id: currentEmployeeId } = useUserInfo();

  const {
    isAccepting,
    isRejecting,
    isInProgress,
    handleAccept,
    handleReject,
    handleRejectButtonClick,
    onModalClose,
    rejectingModalId,
  } = useApproveAbsenceRequestTable();

  const reviewCell = useCallback(
    (props: CellProps<AbsenceRequest>) => {
      if (
        props.row.original.requestApprovalStatus === 'PENDING' &&
        props.row.original.id
      ) {
        return (
          <ReviewActionsCell
            id={props.row.original.id}
            isAccepting={isAccepting}
            isRejecting={isRejecting}
            isInProgress={isInProgress}
            handleAccept={handleAccept}
            handleRejectButtonClick={handleRejectButtonClick}
          />
        );
      } else {
        const approvalRequests = props.row.original.approvalRequests;
        const absenceApprovalStatus = props.row.original.absenceApprovalStatus;

        if (
          approvalRequests.length === 0 &&
          absenceApprovalStatus === AbsenceApprovalStatus.Approved
        )
          return t('autoApproved');

        return <ApprovalAvatars approvalRequests={approvalRequests} />;
      }
    },
    [
      isAccepting,
      isRejecting,
      isInProgress,
      handleAccept,
      handleRejectButtonClick,
      t,
    ],
  );

  const {
    tableState,
    handleFilterChange,
    handlePageIndexChanged,
    handleSortByChanged,
    setTotalItemsCount,
  } = useFlairTableWithPaginationAndFilter<
    AbsenceRequest,
    AbsenceRequestFilterType
  >({
    defaultFilter: defaultFilter,
    storageKey: 'absence_requests_filter',
    createRouteUrl: (queryParams) =>
      mainRoutes.allAbsenceRequests.route
        .withQueryParams(queryParams)
        .create({}),
  });

  const [reportsMode] = useManagerReportsMode('absences');

  const { data, loading, error } = useAbsencesForManagerQuery({
    variables: {
      filter: mapAbsenceRequestFilterInput(tableState.filter, reportsMode),
      sort: mapSortBy(tableState.sortBy),
      pagination: {
        offset: tableState.pageIndex * PAGE_SIZE,
        limit: PAGE_SIZE,
      },
      skipPaginationInfo: tableState.totalItemsCount !== undefined,
    },
  });

  useEffect(() => {
    const totalCount =
      data?.manager.absencesForManager.paginationInfo?.totalCount;
    setTotalItemsCount(totalCount);
  }, [setTotalItemsCount, data]);

  const requests = data
    ? mapAbsenceRequests(
        data?.manager.absencesForManager.items,
        currentEmployeeId,
      ).filter((request) => {
        if (
          request.requestApprovalStatus === ApprovalRequestStatus.Pending &&
          request.absenceApprovalStatus !== AbsenceApprovalStatus.Pending
        ) {
          return false;
        }
        return true;
      })
    : undefined;

  const {
    selectedAbsenceId,
    selectedApprovalRequestId,
    handleAbsenceClick,
    handleClose,
  } = useApprovalRequestSidebar(requests || []);

  const { columns, selectedAbsenceIdForMultiDayAbsence, handleCloseSidebar } =
    useAbsenceRequestTableRenderSettings({
      onLoomVideoClick: setSelectedLoomVideo,
      onAbsenceClick: handleAbsenceClick,
    });

  const renderSettings: ExtraTableRenderSettings<AbsenceRequest> = useMemo(
    () => ({
      columns: {
        ...columns,
        flair__Approval_Status__c: {
          Header: t('status'),
          Cell: (props) => {
            const rejectedRequest = props.row.original.approvalRequests.find(
              (req) => req.approvalStatus === 'REJECTED',
            );
            const hasRejectionComment =
              rejectedRequest && rejectedRequest.approverComment;
            return (
              <div className="d-flex gap-1 align-items-center">
                <AbsenceStatus
                  status={props.row.original.absenceApprovalStatus}
                />
                {hasRejectionComment && (
                  <OverlayTrigger
                    overlay={
                      <Tooltip id="tooltip">
                        {rejectedRequest.approverComment}
                      </Tooltip>
                    }>
                    <span>
                      <FlairIcon
                        role="button"
                        className={'ms-2 cursor-auto text-primary'}
                        icon="chatbubble-outline"
                      />
                    </span>
                  </OverlayTrigger>
                )}
              </div>
            );
          },
        },
      },
      afterPersistentColumns: {
        ActionColumn: {
          Header: t('review'),
          // `reviewCell` is typed against the plain `AbsenceRequest` shape (it never reads
          // `rawExtraFields`), but this slot needs a `Renderer` for the extra-fields-augmented
          // row type; react-table's `Row<D>`/`Cell<D>` aren't covariant in `D` so a plain
          // upcast isn't accepted structurally.
          Cell: reviewCell as unknown as Renderer<
            CellProps<ExtraTableItemType<AbsenceRequest>>
          >,
        },
      },
    }),
    [columns, t, reviewCell],
  );

  const mobileColumns: Column<AbsenceRequest>[] = useMemo(
    () => [
      {
        Header: objectLabels.employee,
        accessor: 'id',
        Cell: (props: CellProps<AbsenceRequest>) => (
          <Col>
            <div>
              <EmployeeWithAvatar employee={props.row.original.requester} />
            </div>
            <Detail>
              <div>
                <AbsenceDateTimeRange absence={props.row.original} />
              </div>
              <div>
                <span className="me-1">{t('mobile.workingTime')}</span>
                {`${props.row.original.workingAmount} ${
                  AbsenceTypeMap[props.row.original.type]
                }`}
              </div>
              <div>
                <span className="me-1">{t('mobile.type')}</span>
                <AbsenceTypeText {...props.row.original} />
              </div>
              <div>
                <span className="me-1">{t('mobile.requestedAt')}</span>
                <FormattedDateTime
                  dateTime={props.row.original.createdDate}
                  format="long"
                />
              </div>
            </Detail>
          </Col>
        ),
      },
      {
        Header: t('review'),
        accessor: 'absenceId',
        Cell: reviewCell,
      },
    ],
    [t, objectLabels, reviewCell],
  );

  if (error) return <ServerError />;

  const renderTable = (requests: AbsenceRequest[]) => {
    if (!requests.length) {
      return <EmptyAbsenceRequestsCard />;
    }
    if (!isMobile) {
      return (
        <ExtraFieldsTable
          useTableContentManual
          objectApiName={ExtraFieldsSupportedObject.Absence}
          renderSettings={renderSettings}
          tableId={ExtraFieldsTableId.AbsencesManager}
          records={requests}
          pageSize={PAGE_SIZE}
          itemsCount={tableState.totalItemsCount}
          initialSortBy={tableState.sortBy}
          onSortByChanged={handleSortByChanged}
          initialPageIndex={tableState.pageIndex}
          onPageIndexChanged={handlePageIndexChanged}
        />
      );
    } else {
      return (
        <TableContentManual
          data={requests}
          columns={mobileColumns}
          pageSize={PAGE_SIZE}
          itemsCount={tableState.totalItemsCount}
          initialSortBy={tableState.sortBy}
          onSortByChanged={handleSortByChanged}
          initialPageIndex={tableState.pageIndex}
          onPageIndexChanged={handlePageIndexChanged}
        />
      );
    }
  };

  return (
    <Card>
      <Header className="h-auto">
        <Row className="align-items-center">
          <Col className="small">
            <AbsenceRequestFiltersConnected
              filter={tableState.filter}
              onChange={handleFilterChange}
            />
          </Col>
        </Row>
      </Header>

      {requests !== undefined && !loading ? (
        renderTable(requests)
      ) : (
        <SkeletonTable className="card-table" columns={5} rows={10} />
      )}

      {selectedLoomVideo && (
        <LoomVideoPreviewInModal
          loomVideo={selectedLoomVideo}
          onHide={() => setSelectedLoomVideo(null)}
        />
      )}

      <ApproverCommentModal
        requestId={rejectingModalId}
        onClose={onModalClose}
        onRejected={handleReject}
      />
      {selectedAbsenceIdForMultiDayAbsence && (
        <MultiDayAbsenceDetails
          absenceId={selectedAbsenceIdForMultiDayAbsence}
          show={true}
          onClose={handleCloseSidebar}
        />
      )}

      {(selectedAbsenceId || selectedApprovalRequestId) && (
        <ApprovalRequestDetailsSidebar
          show={true}
          onClose={handleClose}
          approvalRequestId={selectedApprovalRequestId ?? undefined}
          relatedRecordId={selectedAbsenceId ?? undefined}
          relatedObjectNameStr={RelatedObjectNames.Absence}
        />
      )}
    </Card>
  );
};

const Header = styled(Card.Header)({
  height: 'auto',
  minHeight: 60,
});

const Detail = styled.div({
  marginLeft: 32,
});
